// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { type ApiErrorPayloadCommon, isApiError } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

const getEnrollStatusQueryKey = 'get-enroll-status';

interface GetEnrollStatusResponse {
  enrolled: boolean;
}

const getEnrollStatusQuery = async (
  codingContext: CodingContext | undefined,
  setApiError: (error: ApiErrorPayloadCommon) => void,
): Promise<boolean> => {
  try {
    const response = await apiClient.get<GetEnrollStatusResponse>('enroll/status', codingContext);
    return response.enrolled;
  } catch (error: unknown) {
    if (isApiError(error)) {
      setApiError(error);
    } else {
      setApiError({ error: { code: 'unknown', message: 'Unknown error' } });
    }
    throw error;
  }
};

export const useGetEnrollStatusQuery = () => {
  const { codingContext } = usePlatform();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | undefined>(undefined);
  const query = useQuery({
    queryKey: [getEnrollStatusQueryKey],
    queryFn: () => getEnrollStatusQuery(codingContext, setApiError),
    enabled: enabled,
    retry: false,
  });
  return {
    enrolled: query.data ?? false,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    setEnabled: () => setEnabled(true),
    apiError,
  };
};
